import { ofType } from 'redux-observable';
import { EMPTY, timer, merge, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  takeUntil,
  switchMap,
  finalize,
  mergeMap,
  tap,
} from 'rxjs/operators';

import { subscribeSpot30mtwapPrice, unsubscribeSpot30mtwapPrice } from 'actions/socket';
import {
  SUBSCRIBE_SPOT_30MTWAP_PRICE,
  UNSUBSCRIBE_SPOT_30MTWAP_PRICE,
  UNSUBSCRIBE_ALL,
  SOCKET_CONNECTED,
  DISCONNECT_SOCKET,
} from 'actionTypes/socket';
import { TRADE_CONSTANTS } from 'actionTypes/trade';
import { TWAP_DISPLAY_THRESHOLD_MINUTES } from 'constants/constants';
import { TIME_IN_MS } from 'constants/dateTime';
import { addLogsToSentry } from 'helpers/sentry';
import { isOptions } from 'helpers/utils';
import { socketActiveSelector } from 'selectors/socketSelectors';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import {
  setSelectedProductSpotTwap,
  showTwap,
  hideTwap,
} from 'stores/spot30mTwap/spot30mTwap.store';

import { wsSubject } from './socket';

const payloadGenerator = (type, name, symbols) => {
  return {
    type,
    payload: {
      channels: [
        {
          name,
          symbols,
        },
      ],
    },
  };
};

const spot30mtwapChannel = symbol =>
  wsSubject.multiplex(
    () => payloadGenerator('subscribe', 'spot_30mtwap_price', symbol),
    () => payloadGenerator('unsubscribe', 'spot_30mtwap_price', symbol),
    message => message.type === 'spot_30mtwap_price'
  );

const selectedProductSpot30mtwapEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_SPOT_30MTWAP_PRICE),
    filter(() => Boolean(socketActiveSelector(state$.value))),
    switchMap(() => {
      const selectedProduct = selectedProductSelector(state$.value);
      const symbol = selectedProduct?.spot_index?.symbol;

      if (!symbol) {
        return EMPTY;
      }

      return spot30mtwapChannel([symbol]).pipe(
        tap(data => setSelectedProductSpotTwap(data.price)),
        takeUntil(action$.pipe(ofType(UNSUBSCRIBE_SPOT_30MTWAP_PRICE, UNSUBSCRIBE_ALL))),
        finalize(() => setSelectedProductSpotTwap(null)),
        catchError(err => {
          addLogsToSentry(null, 'SPOT_30MTWAP_ERROR', err);
          return EMPTY;
        })
      );
    })
  );

// Epic to manage TWAP display timing and socket subscriptions
const twapDisplayTimerEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRADE_CONSTANTS.PRODUCT_SELECTED, SOCKET_CONNECTED),
    filter(() => {
      const socketActive = Boolean(socketActiveSelector(state$.value));
      const selectedProduct = selectedProductSelector(state$.value);
      const isValidProduct =
        isOptions(selectedProduct?.contract_type) &&
        Boolean(selectedProduct?.settlement_time);

      return socketActive && isValidProduct;
    }),
    switchMap(() => {
      const product = selectedProductSelector(state$.value);
      const expiryDate = product?.settlement_time;
      const timeRemainingUntilExpiryInMs = new Date(expiryDate).getTime() - Date.now();
      const thresholdInMs = TWAP_DISPLAY_THRESHOLD_MINUTES * TIME_IN_MS.ONE_MINUTE;

      // If already expired, don't show TWAP
      if (timeRemainingUntilExpiryInMs <= 0) {
        hideTwap();
        return of(unsubscribeSpot30mtwapPrice());
      }

      const timers = [];

      // Determine when to show TWAP
      if (timeRemainingUntilExpiryInMs > thresholdInMs) {
        // Show TWAP after threshold delay
        const showTimer = timer(timeRemainingUntilExpiryInMs - thresholdInMs).pipe(
          mergeMap(() => {
            showTwap();
            return of(subscribeSpot30mtwapPrice());
          })
        );
        timers.push(showTimer);
      } else {
        // We're within threshold, show immediately
        showTwap();
        const immediateShow = of(subscribeSpot30mtwapPrice());
        timers.push(immediateShow);
      }

      // Hide TWAP at expiry
      const hideTimer = timer(timeRemainingUntilExpiryInMs).pipe(
        mergeMap(() => {
          hideTwap();
          return of(unsubscribeSpot30mtwapPrice());
        })
      );
      timers.push(hideTimer);

      return merge(...timers).pipe(
        takeUntil(
          action$.pipe(ofType(TRADE_CONSTANTS.PRODUCT_SELECTED, DISCONNECT_SOCKET))
        ),
        catchError(err => {
          addLogsToSentry(null, 'TWAP_DISPLAY_TIMER_ERROR', err);
          hideTwap();
          return of(unsubscribeSpot30mtwapPrice());
        })
      );
    })
  );

// Epic to handle non-options products or products without settlement time
const hidetwapForNonOptionsEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRADE_CONSTANTS.PRODUCT_SELECTED),
    map(() => selectedProductSelector(state$.value)),
    filter(
      product => !product || !isOptions(product.contract_type) || !product.settlement_time
    ),
    mergeMap(() => {
      hideTwap();
      return of(unsubscribeSpot30mtwapPrice());
    }),
    catchError(err => {
      addLogsToSentry(null, 'HIDE_TWAP_NON_OPTIONS_ERROR', err);
      return EMPTY;
    })
  );

// Epic to handle socket disconnection cleanup
const twapSocketDisconnectEpic = action$ =>
  action$.pipe(
    ofType(DISCONNECT_SOCKET),
    mergeMap(() => {
      hideTwap();
      return of(unsubscribeSpot30mtwapPrice());
    }),
    catchError(err => {
      addLogsToSentry(null, 'TWAP_SOCKET_DISCONNECT_ERROR', err);
      return EMPTY;
    })
  );

export {
  selectedProductSpot30mtwapEpic,
  twapDisplayTimerEpic,
  hidetwapForNonOptionsEpic,
  twapSocketDisconnectEpic,
};
